import Button from '@repo/ui/button'
import { AirplaneIcon, DeleteIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createTripStep } from '../../../api/trip-bookings/create-trip-step'
import { deleteTripStep } from '../../../api/trip-bookings/delete-trip-step'
import { getTripSteps } from '../../../api/trip-bookings/get-trip-steps'
import { updateTripStep } from '../../../api/trip-bookings/update-trip-step'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TransportTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TransportMeans } from '../../../lib/types/models/transport-means/transport-means'
import { DepartureTimeTripProviderInput } from '../../../lib/types/models/trip-booking/departure-time-trip-provider'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import TripStepCard from './trip-step-card'
import TripStepForm from './trip-step-form'
import { mapTripStepInputToRequest, mapTripStepToInput } from './utils'

type Props = {
  transportMean: TransportMeans
  tripBookingId: number
  type: TripStepTypeEnum
  directionality: TransportTypeEnum
}

const TripBookingTripSteps = ({ transportMean, tripBookingId, type, directionality }: Props) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit' | 'delete',
    TripStepTypes.Full
  >(['create', 'edit', 'delete'])
  const queryClient = useQueryClient()
  const queryKey = ['trip-step', { type, tripBookingId }]

  const { data: tripSteps } = useQuery({
    queryKey,
    queryFn: () => getTripSteps({ tripBookingId, filter: { type } })
  })

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createTripStep,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      closeModel('create')
      toast.success('Étape créée avec succès')
    }
  })

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: updateTripStep,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      closeModel('edit')
      toast.success('Étape modifiée avec succès')
    }
  })

  const { mutateAsync: deleteMutation, isPending: isDeletePending } = useMutation({
    mutationFn: deleteTripStep,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      closeModel('delete')
      toast.success('Étape supprimée avec succès')
    }
  })

  return (
    <div>
      <ActionTableWrapper
        title={
          type === TripStepTypeEnum.INBOUND
            ? directionality === TransportTypeEnum.ONE_WAY
              ? 'Étapes - Aller Simple'
              : 'Étapes - Aller'
            : 'Étapes - Retour'
        }
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter une étape'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <div className="flex gap-4 overflow-x-auto">
          {tripSteps?.data.map((tripStep, index) => (
            <TripStepCard
              key={tripStep.id}
              transportMeans={transportMean}
              title={`Étape - ${index + 1}`}
              tripStep={tripStep}
              onEdit={() => toggleModal('edit', tripStep)}
              onDelete={() => toggleModal('delete', tripStep)}
              className="flex-shrink-0 w-[calc(33.33%-1rem)] min-w-[calc(33.33%-1rem)]" // for 3 items per row
            />
          ))}
        </div>
        <Modal
          isOpen={modalState.create}
          title={`Ajouter une étape ${type === TripStepTypeEnum.INBOUND ? 'Aller' : 'Retour'}`}
          icon={AirplaneIcon}
          hideActions
          size={'medium'}
          onCancel={() => closeModel('create')}
        >
          <TripStepForm
            mode={type === TripStepTypeEnum.INBOUND ? 'departure' : 'arrival'}
            defaultValues={
              type === TripStepTypeEnum.INBOUND
                ? { departureTimeZone: 'Europe/Paris' }
                : { arrivalTimeZone: 'Europe/Paris' }
            }
            className={MODAL_BODY_CLASSNAME}
            tripBookingId={tripBookingId}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: (values) =>
                createMutation({
                  type,
                  tripBookingId,
                  ...values,
                  departureTimeTripProviders:
                    values.departureTimeTripProviders as DepartureTimeTripProviderInput[]
                })
            })}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Modifier une étape'}
            icon={AirplaneIcon}
            hideActions
            size={'medium'}
            onCancel={() => closeModel('edit')}
          >
            <TripStepForm
              mode={type === TripStepTypeEnum.INBOUND ? 'departure' : 'arrival'}
              className={MODAL_BODY_CLASSNAME}
              defaultValues={mapTripStepToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: (values) =>
                  updateMutation({
                    ...mapTripStepInputToRequest(values, type),
                    id: selectedData.id,
                    tripBookingId
                  })
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Confirmer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                await deleteMutation({ id: selectedData.id, tripBookingId })
                toggleModal('delete')
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Supprimer une étape'}
            icon={DeleteIcon}
            isLoading={isDeletePending}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </div>
  )
}

export default TripBookingTripSteps
